/**
 * face-api.js yardımcı katmanı — tarayıcıda çalışır.
 *
 * Amaç:
 *  - modelleri /models/ altından bir kez yüklemek
 *  - kameradan tek yüzü tespit + landmark + 128-d descriptor çıkarmak
 *  - descriptor'lar arasında L2 mesafesiyle en yakın eşleşmeyi bulmak
 *  - basit anti-spoof / kalite kontrolü (yüz boyutu + tek yüz kuralı)
 *
 * Ham foto asla sunucuya yollanmaz — sadece 128 float descriptor.
 */
import * as faceapi from "face-api.js";

let _modelsLoaded = false;
let _loadingPromise = null;

export async function ensureFaceModels() {
  if (_modelsLoaded) return;
  if (_loadingPromise) return _loadingPromise;
  const MODEL_URL = "/models";
  const t0 = performance.now();
  console.info("[face-api] modeller yükleniyor…", MODEL_URL);
  _loadingPromise = Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
    faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
  ])
    .then(() => {
      _modelsLoaded = true;
      console.info("[face-api] modeller yüklendi", Math.round(performance.now() - t0), "ms");
    })
    .catch((err) => {
      // Reset so a retry can start a fresh load
      _loadingPromise = null;
      console.error("[face-api] model yükleme hatası:", err);
      throw err;
    });
  return _loadingPromise;
}

export function resetFaceModels() {
  // Testing / retry helper — sadece flag'i sıfırlar
  _modelsLoaded = false;
  _loadingPromise = null;
}

/** TinyFaceDetector için düşük eşik + yeterli hız için 320px input. */
export function detectorOptions() {
  return new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });
}

/**
 * Video ya da image element'inden tek en iyi yüzü + descriptor çıkar.
 * Dönüş:
 *   null                → yüz bulunamadı
 *   { descriptor, box, score, multiple }
 *     multiple=true      → birden fazla yüz görülüyor (kayıt reddedilmeli)
 */
export async function detectSingleFaceWithDescriptor(mediaEl) {
  await ensureFaceModels();
  const all = await faceapi
    .detectAllFaces(mediaEl, detectorOptions())
    .withFaceLandmarks()
    .withFaceDescriptors();
  if (!all || all.length === 0) return null;
  // En büyük yüzü seç
  const best = all.slice().sort((a, b) => (b.detection.box.area || 0) - (a.detection.box.area || 0))[0];
  return {
    descriptor: Array.from(best.descriptor),  // Float32Array -> plain array
    box: best.detection.box,
    score: best.detection.score,
    multiple: all.length > 1,
  };
}

/** Kalite kontrolü — yüz kırpıntısı, düşük skor, çok küçük/kenarda ise reddet. */
export function faceQualityIssues(res, videoEl) {
  if (!res) return ["Yüz algılanmadı"];
  const issues = [];
  if (res.multiple) issues.push("Birden fazla yüz görülüyor");
  if (res.score < 0.5) issues.push("Yüz skoru düşük");
  const w = videoEl?.videoWidth || 640;
  const h = videoEl?.videoHeight || 480;
  const b = res.box;
  const faceArea = b.width * b.height;
  const frameArea = w * h;
  if (faceArea / frameArea < 0.06) issues.push("Yüz çok küçük — kameraya yaklaşın");
  if (b.x < 8 || b.y < 8 || b.x + b.width > w - 8 || b.y + b.height > h - 8) {
    issues.push("Yüz kadraj kenarında — ortalayın");
  }
  return issues;
}

/** İki 128-d descriptor arasındaki L2 (Euclidean) mesafesi. */
export function l2Distance(a, b) {
  if (!a || !b || a.length !== b.length) return Infinity;
  let s = 0;
  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i];
    s += d * d;
  }
  return Math.sqrt(s);
}

/**
 * Verilen 'query' descriptor için en yakın kişi kaydını bul.
 * profiles: [{ employee_id, descriptors: number[][] }]
 * threshold: <=  eşik ise eşleşme kabul (varsayılan face-api önerisi 0.6, biz 0.5 daha sıkı)
 * Dönüş: { employee_id, distance } | null
 */
export function findBestMatch(query, profiles, threshold = 0.5) {
  let best = null;
  for (const p of profiles) {
    for (const d of p.descriptors || []) {
      const dist = l2Distance(query, d);
      if (!best || dist < best.distance) {
        best = { employee_id: p.employee_id, distance: dist };
      }
    }
  }
  if (!best || best.distance > threshold) return null;
  return best;
}

/**
 * Kısa "canlılık" ipucu: son N örnek arasında descriptor değişkenliği (varyans) + hafif hareket bekleniyor.
 * Fotoğraf tutmak durağan bir descriptor üretir; canlı yüz ufak varyasyonlar gösterir.
 */
export function livenessScore(sampleDescriptors) {
  if (!sampleDescriptors || sampleDescriptors.length < 2) return 0;
  const n = sampleDescriptors.length;
  const dim = sampleDescriptors[0].length;
  const mean = new Array(dim).fill(0);
  for (const d of sampleDescriptors) for (let i = 0; i < dim; i++) mean[i] += d[i];
  for (let i = 0; i < dim; i++) mean[i] /= n;
  let variance = 0;
  for (const d of sampleDescriptors) {
    for (let i = 0; i < dim; i++) {
      const diff = d[i] - mean[i];
      variance += diff * diff;
    }
  }
  return variance / n; // Genelde canlı yüzde ~0.01-0.03, statik fotoğrafta ~ < 0.001
}
